import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AsyncSelect,
  Badge,
  ButtonText,
  Flex,
  FormField,
  Text,
} from '@percent/lemonade'
import type { SelectOption } from '@percent/lemonade'
import type { AssigneeSearchResult } from '../../types/permissions.types'
import { getAssigneeTypeLabel } from '../domainAssignees/utils'
import type { AccountOrGroupPickerProps } from './AccountOrGroupPicker.types'

const MIN_SEARCH_LENGTH = 3

const toOptionValue = ({ type, id }: AssigneeSearchResult) => `${type}:${id}`

export function AccountOrGroupPicker({
  searchAssignees,
  value,
  onChange,
  dataTestId,
}: AccountOrGroupPickerProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AssigneeSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  // Ignores responses to superseded searches so a slow earlier query can't overwrite a later one
  const latestSearch = useRef(0)

  const searchOptions: SelectOption[] = results.map((result) => ({
    value: toOptionValue(result),
    label: result.name,
    badge: { text: getAssigneeTypeLabel(result.type, t) },
  }))

  const handleSearch = async (nextQuery: string) => {
    setQuery(nextQuery)

    if (nextQuery.length < MIN_SEARCH_LENGTH) {
      setResults([])

      return
    }

    const searchId = ++latestSearch.current

    setIsSearching(true)

    try {
      const found = await searchAssignees(nextQuery)

      if (searchId === latestSearch.current) {
        setResults(found)
      }
    } catch {
      if (searchId === latestSearch.current) {
        setResults([])
      }
    } finally {
      if (searchId === latestSearch.current) {
        setIsSearching(false)
      }
    }
  }

  const handleSelect = (option: SelectOption | null) => {
    const match = results.find(
      (result) => option && toOptionValue(result) === option.value
    )

    if (match) {
      onChange(match)
      setQuery('')
      setResults([])
    }
  }

  return (
    <FormField label={t('modal.assignRole.search.label')} necessity="required">
      {value ? (
        <Flex
          direction="row"
          gap="xs"
          align="center"
          data-testid={`${dataTestId}-selected-assignee`}
        >
          <Text size="small" fontWeight="medium">
            {value.name}
          </Text>
          <Badge variant="informative">
            {getAssigneeTypeLabel(value.type, t)}
          </Badge>
          <ButtonText onClick={() => onChange(null)}>
            {t('modal.assignRole.search.change')}
          </ButtonText>
        </Flex>
      ) : (
        <AsyncSelect
          data-testid={`${dataTestId}-search-select`}
          placeholder={t('modal.assignRole.search.placeholder')}
          options={searchOptions}
          setQuery={handleSearch}
          query={query}
          onChange={handleSelect}
          loading={isSearching}
          noResultsFoundText={t('modal.assignRole.search.noResults')}
          clearQueryOnSelect
          minSearchLength={MIN_SEARCH_LENGTH}
          minSearchLengthMessage={t('modal.assignRole.search.minLength')}
        />
      )}
    </FormField>
  )
}
